// frontend/src/components/jobPlacement/JobSearchDataSheet.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const JobSearchDataSheet = ({ consumer, jobPlacement, onBack }) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Date range for filtering/PDF generation
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Navigate to create new outreach with consumer pre-selected
  const handleLogContact = () => {
    navigate(`/crm/placement-outreach?action=new&consumer_id=${consumer.consumer_id}`);
  };

  // Load contacts
  const loadContacts = async () => {
    if (!consumer?.consumer_id || !user?.tenant_id) return;

    setLoading(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await secureAxios.get(
        `${apiBase}/api/job-search-contacts/consumer/${consumer.consumer_id}`,
        { params }
      );
      setContacts(res.data);
    } catch (err) {
      console.error("Error loading contacts:", err);
      toast.error("Failed to load job search contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [consumer?.consumer_id, user?.tenant_id, startDate, endDate]);

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await secureAxios.get(
        `${apiBase}/api/job-search-contacts/crp33/${consumer.consumer_id}`,
        { params }
      );

      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
        toast.success(
          `CRP-33 generated with ${res.data.contact_count} contacts`
        );
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate CRP-33 PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (
      !window.confirm(
        `Delete contact with ${contact.employer_name} on ${formatDate(
          contact.contact_date
        )}?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/job-search-contacts/${contact.contact_id}`,
        { params: { tenant_id: user.tenant_id } }
      );
      toast.success("Contact deleted");
      loadContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      toast.error("Failed to delete contact");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };


  // Render list view
  return (
    <div className="container my-4">
    <div className="job-search-data-sheet">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="bi bi-file-earmark-text me-2"></i>
          <span className="d-none d-sm-inline">CRP-33: </span>Job Search Data Sheet
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>

      {/* Consumer Info */}
      <div className="card bg-light mb-3">
        <div className="card-body py-2 px-3">
          <div className="mb-2">
            <small className="text-muted d-block">Consumer:</small>
            <div className="fw-bold">{consumer?.name}</div>
          </div>
          {jobPlacement && jobPlacement.vocational_goal && (
            <div>
              <small className="text-muted d-block">Vocational Goal:</small>
              <div className="small">{jobPlacement.vocational_goal}</div>
            </div>
          )}
        </div>
      </div>

      {/* Date Range Filter & Actions */}
      <div className="card mb-3">
        <div className="card-body py-2 px-3">
          {/* Mobile: Stack vertically */}
          <div className="d-md-none">
            <div className="row g-2 mb-2">
              <div className="col-6">
                <label className="form-label small mb-1">Start Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-6">
                <label className="form-label small mb-1">End Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {(startDate || endDate) && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              )}
              <button
                className="btn btn-success btn-sm"
                onClick={handleGeneratePDF}
                disabled={generatingPDF}
              >
                {generatingPDF ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <>
                    <i className="bi bi-file-pdf me-1"></i>
                    CRP-33
                  </>
                )}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleLogContact}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Contact
              </button>
            </div>
          </div>

          {/* Desktop: Side by side */}
          <div className="d-none d-md-block">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label small mb-1">Start Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">End Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                  {(startDate || endDate) && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Dates
                    </button>
                  )}
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleGeneratePDF}
                    disabled={generatingPDF}
                  >
                    {generatingPDF ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-pdf me-1"></i>
                        Generate CRP-33
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleLogContact}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Log Contact
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contacts Table/Cards */}
      {loading ? (
        <div className="text-center py-3 py-md-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-3 py-md-5">
          <i
            className="bi bi-telephone text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Job Search Contacts</h5>
          <p className="text-muted small">
            {startDate || endDate
              ? "No contacts found in the selected date range"
              : "Start logging employer contacts to track job search progress"}
          </p>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleLogContact}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Log First Contact
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-striped table-hover table-sm">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "100px" }}>Date</th>
                  <th>Employer</th>
                  <th>Result of Contact</th>
                  <th>Comments</th>
                  <th style={{ width: "80px" }} className="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.contact_id}>
                    <td>{formatDate(contact.contact_date)}</td>
                    <td>
                      <strong>{contact.employer_name}</strong>
                      {contact.employer_company_name &&
                        contact.employer_company_name !==
                          contact.employer_name && (
                          <small className="text-muted d-block">
                            {contact.employer_company_name}
                          </small>
                        )}
                    </td>
                    <td>{contact.result_of_contact || "-"}</td>
                    <td>
                      {contact.comments ? (
                        <span title={contact.comments}>
                          {contact.comments.length > 50
                            ? contact.comments.substring(0, 50) + "..."
                            : contact.comments}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDeleteContact(contact)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-md-none">
            {contacts.map((contact) => (
              <div key={contact.contact_id} className="card mb-2">
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-bold text-truncate">
                        {contact.employer_name}
                      </div>
                      {contact.employer_company_name &&
                        contact.employer_company_name !== contact.employer_name && (
                          <small className="text-muted d-block text-truncate">
                            {contact.employer_company_name}
                          </small>
                        )}
                    </div>
                    <small className="text-muted ms-2 text-nowrap">
                      {formatDate(contact.contact_date)}
                    </small>
                  </div>
                  
                  {contact.result_of_contact && (
                    <div className="small mb-1">
                      <span className="badge bg-secondary me-1">Result</span>
                      {contact.result_of_contact}
                    </div>
                  )}
                  
                  {contact.comments && (
                    <div className="small text-muted mb-2" style={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {contact.comments}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDeleteContact(contact)}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mt-3">
            <span className="text-muted small">
              {contacts.length} contact{contacts.length !== 1 ? "s" : ""} found
              {(startDate || endDate) && " in selected date range"}
            </span>
            <button
              className="btn btn-success btn-sm"
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
            >
              <i className="bi bi-file-pdf me-1"></i>
              <span className="d-none d-sm-inline">Generate </span>CRP-33 PDF
            </button>
          </div>
        </>
      )}

      {/* Info Note - Collapsible on mobile */}
      <div className="alert alert-secondary mt-3 py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-info-circle me-2"></i>
          <span className="d-none d-md-inline">
            <strong>About CRP-33:</strong> The CRP-33 form tracks all employer contacts made during the job search process. 
            Use the date range filters to generate a PDF report for a specific period.
          </span>
          <small className="d-md-none">
            <strong>CRP-33</strong> tracks employer contacts during job search.
          </small>
        </div>
      </div>
    </div>
    </div>
  );
};

export default JobSearchDataSheet;